import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import invoiceService from '../../../services/invoiceService';
import vendorService from '../../../services/vendorService';
import { formatPrice, formatDate } from '../../../utils';
import InvoiceForm from './InvoiceForm';
import InvoiceDetails from './InvoiceDetails';

const InvoiceManagement = () => {
  const { user, userProfile } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    clientName: '',
    fromDate: '',
    toDate: ''
  });
  const [hasPaidSubscription, setHasPaidSubscription] = useState(false);

  // Load vendor data and check subscription
  useEffect(() => {
    const loadVendorData = async () => {
      if (!user?.id || userProfile?.role !== 'vendor') return;

      try {
        setLoading(true);
        setError(null);

        // Get vendor profile
        const vendorResult = await vendorService.getVendors({ 
          filters: { user_id: user.id } 
        });

        if (vendorResult?.success && vendorResult?.data?.length > 0) {
          const vendor = vendorResult.data[0];
          setVendorProfile(vendor);

          // Check subscription status
          const subscriptionResult = await invoiceService.checkPaidSubscription();
          setHasPaidSubscription(subscriptionResult?.success && subscriptionResult?.data);

          if (subscriptionResult?.success && subscriptionResult?.data) {
            // Load invoices and stats
            await loadInvoices(vendor.id);
            await loadInvoiceStats(vendor.id);
          }
        }
      } catch (error) {
        setError('Failed to load vendor data');
        console.log('Error loading vendor data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVendorData();
  }, [user?.id, userProfile?.role]);

  // Load invoices
  const loadInvoices = async (vendorId) => {
    try {
      const result = await invoiceService.getVendorInvoices(vendorId, filters);
      if (result?.success) {
        setInvoices(result.data || []);
      } else {
        setError(result?.error || 'Failed to load invoices');
      }
    } catch (error) {
      setError('Failed to load invoices');
      console.log('Error loading invoices:', error);
    }
  };

  // Load invoice statistics
  const loadInvoiceStats = async (vendorId) => {
    try {
      const result = await invoiceService.getInvoiceStats(vendorId);
      if (result?.success) {
        setInvoiceStats(result.data);
      }
    } catch (error) {
      console.log('Error loading invoice stats:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    if (vendorProfile?.id) {
      loadInvoices(vendorProfile.id);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      status: '',
      clientName: '',
      fromDate: '',
      toDate: ''
    });
    if (vendorProfile?.id) {
      loadInvoices(vendorProfile.id);
    }
  };

  // Handle invoice creation
  const handleInvoiceCreated = (newInvoice) => {
    setInvoices(prev => [newInvoice, ...prev]);
    setShowCreateForm(false);
    if (vendorProfile?.id) {
      loadInvoiceStats(vendorProfile.id);
    }
  };

  // Handle invoice update
  const handleInvoiceUpdated = (updatedInvoice) => {
    setInvoices(prev => prev?.map(inv => 
      inv?.id === updatedInvoice?.id ? updatedInvoice : inv
    ));
    if (vendorProfile?.id) {
      loadInvoiceStats(vendorProfile.id);
    }
  };

  // Handle invoice deletion
  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const result = await invoiceService.deleteInvoice(invoiceId);
      if (result?.success) {
        setInvoices(prev => prev?.filter(inv => inv?.id !== invoiceId));
        if (vendorProfile?.id) {
          loadInvoiceStats(vendorProfile.id);
        }
      } else {
        setError(result?.error || 'Failed to delete invoice');
      }
    } catch (error) {
      setError('Failed to delete invoice');
      console.log('Error deleting invoice:', error);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (invoiceId, newStatus) => {
    try {
      const result = await invoiceService.updateInvoice(invoiceId, { status: newStatus });
      if (result?.success) {
        handleInvoiceUpdated(result.data);
      } else {
        setError(result?.error || 'Failed to update status');
      }
    } catch (error) {
      setError('Failed to update status');
      console.log('Error updating status:', error);
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-600';
      case 'sent': return 'bg-blue-100 text-blue-600';
      case 'paid': return 'bg-green-100 text-green-600';
      case 'overdue': return 'bg-red-100 text-red-600';
      case 'cancelled': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Check if invoice is overdue
  const isOverdue = (invoice) => {
    if (invoice?.status !== 'sent') return false;
    return new Date(invoice?.due_date) < new Date();
  };

  // Handle invoice view
  const handleViewInvoice = (invoice) => {
    try {
      if (!invoice || !invoice.id) {
        setError('Invalid invoice data');
        return;
      }
      setSelectedInvoice(invoice);
    } catch (error) {
      console.log('Error opening invoice:', error);
      setError('Failed to open invoice');
    }
  };

  // Handle closing invoice details
  const handleCloseInvoiceDetails = () => {
    setSelectedInvoice(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading invoice management...</p>
        </div>
      </div>
    );
  }

  if (userProfile?.role !== 'vendor') {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">Access Denied</h3>
        <p className="text-text-secondary">Invoice management is only available for vendor accounts.</p>
      </div>
    );
  }

  if (!hasPaidSubscription) {
    return (
      <div className="text-center py-12">
        <Icon name="Crown" size={48} className="text-primary mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">Premium Feature</h3>
        <p className="text-text-secondary mb-4">
          Invoice management is available for paid subscription plans only.
        </p>
        <Button variant="primary" onClick={() => window.location.href = '/vendor-dashboard?tab=subscription'}>
          Upgrade Your Plan
        </Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-text-primary mb-2">Error</h3>
        <p className="text-text-secondary mb-4">{error}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-2xl text-text-primary">
            Invoice Management
          </h2>
          <p className="text-text-secondary">
            Create, manage, and track your invoices
          </p>
        </div>
        <Button
          variant="primary"
          iconName="Plus"
          onClick={() => setShowCreateForm(true)}
        >
          Create Invoice
        </Button>
      </div>

      {/* Statistics Cards */}
      {invoiceStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Icon name="FileText" size={24} className="text-blue-600" />
              </div>
              <span className="text-blue-600 text-sm font-medium">Total</span>
            </div>
            <div className="text-2xl font-bold text-text-primary">{invoiceStats?.totalInvoices || 0}</div>
            <div className="text-text-secondary text-sm">Total Invoices</div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Icon name="DollarSign" size={24} className="text-green-600" />
              </div>
              <span className="text-green-600 text-sm font-medium">Paid</span>
            </div>
            <div className="text-2xl font-bold text-text-primary">
              {formatPrice(invoiceStats?.paidAmount || 0)}
            </div>
            <div className="text-text-secondary text-sm">{invoiceStats?.paidCount || 0} invoices</div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={24} className="text-yellow-600" />
              </div>
              <span className="text-yellow-600 text-sm font-medium">Pending</span>
            </div>
            <div className="text-2xl font-bold text-text-primary">
              {formatPrice(invoiceStats?.pendingAmount || 0)}
            </div>
            <div className="text-text-secondary text-sm">{invoiceStats?.sentCount || 0} invoices</div>
          </div>

          <div className="bg-white rounded-xl shadow-soft p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} className="text-red-600" />
              </div>
              <span className="text-red-600 text-sm font-medium">Overdue</span>
            </div>
            <div className="text-2xl font-bold text-text-primary">
              {formatPrice(invoiceStats?.overdueAmount || 0)}
            </div>
            <div className="text-text-secondary text-sm">{invoiceStats?.overdueCount || 0} invoices</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Status</label>
            <select
              value={filters?.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">Client Name</label>
            <input
              type="text"
              value={filters?.clientName}
              onChange={(e) => handleFilterChange('clientName', e.target.value)}
              placeholder="Search by client name"
              className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">From Date</label>
            <input
              type="date"
              value={filters?.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">To Date</label>
            <input
              type="date"
              value={filters?.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full px-3 py-2 border border-border-light rounded-lg focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        <div className="flex space-x-2 mt-4">
          <Button variant="primary" size="sm" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outline" size="sm" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl shadow-soft">
        <div className="p-6 border-b border-border-light">
          <h3 className="font-heading font-semibold text-lg text-text-primary">
            Recent Invoices ({invoices?.length || 0})
          </h3>
        </div>

        {invoices?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" size={48} className="text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Invoices Found</h3>
            <p className="text-text-secondary mb-4">
              {Object.values(filters).some(f => f) ? 'No invoices match your current filters.' : 'Start by creating your first invoice.'}
            </p>
            {!Object.values(filters).some(f => f) && (
              <Button variant="primary" onClick={() => setShowCreateForm(true)}>
                Create Your First Invoice
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border-light">
            {invoices?.map((invoice) => (
              <div key={invoice?.id} className="p-6 hover:bg-surface-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h4 className="font-heading font-semibold text-lg text-text-primary">
                        {invoice?.invoice_number}
                      </h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        isOverdue(invoice) ? 'bg-red-100 text-red-600' : getStatusBadgeColor(invoice?.status)
                      }`}>
                        {isOverdue(invoice) ? 'Overdue' : invoice?.status}
                      </span>
                    </div>
                    <p className="text-text-secondary mb-2">{invoice?.client_name}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-text-muted">Invoice Date:</span>
                        <p className="text-text-primary font-medium">{formatDate(invoice?.invoice_date)}</p>
                      </div>
                      <div>
                        <span className="text-text-muted">Due Date:</span>
                        <p className={`font-medium ${
                          isOverdue(invoice) ? 'text-red-600' : 'text-text-primary'
                        }`}>
                          {formatDate(invoice?.due_date)}
                        </p>
                      </div>
                      <div>
                        <span className="text-text-muted">Amount:</span>
                        <p className="text-text-primary font-medium">
                          {formatPrice(invoice?.total_amount)}
                        </p>
                      </div>
                      <div>
                        <span className="text-text-muted">Items:</span>
                        <p className="text-text-primary font-medium">
                          {invoice?.invoice_items?.length || 0} items
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Eye"
                      onClick={() => handleViewInvoice(invoice)}
                    >
                      View
                    </Button>
                    
                    {invoice?.status === 'draft' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(invoice?.id, 'sent')}
                      >
                        Send
                      </Button>
                    )}
                    
                    {invoice?.status === 'sent' && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleStatusUpdate(invoice?.id, 'paid')}
                      >
                        Mark Paid
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => handleDeleteInvoice(invoice?.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-xl text-text-primary">
                Create New Invoice
              </h3>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={() => setShowCreateForm(false)}
              />
            </div>
            <InvoiceForm
              vendorId={vendorProfile?.id}
              onSuccess={handleInvoiceCreated}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-border-light flex-shrink-0">
              <h3 className="font-heading font-semibold text-xl text-text-primary">
                Invoice Details
              </h3>
              <Button
                variant="outline"
                size="sm"
                iconName="X"
                onClick={handleCloseInvoiceDetails}
              />
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <InvoiceDetails
                invoice={selectedInvoice}
                onUpdate={handleInvoiceUpdated}
                onClose={handleCloseInvoiceDetails}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceManagement;